import React from 'react';
import { MapPin } from '@phosphor-icons/react';
import { Autocomplete, AutocompleteHandle } from '../Autocomplete';
// Direct import (not the barrel) so bundlers don't pull MobileBottomNav/MobileMenuModal, which
// other files only import dynamically for code-splitting, into whatever chunk this static import
// lands in. See components/navigation/index.ts.
import { Logo } from '../navigation/Logo';
import { navIndexMessages } from '../navigation/nav-live-index/nav-index-messages';
import type { IntlShape } from 'react-intl';

export interface SearchNavbarProps {
  intl: IntlShape;
  formatMessage: IntlShape['formatMessage'];
  clearSearch: (event?: React.MouseEvent<HTMLAnchorElement>) => Promise<void>;
  autocompleteRef?: React.RefObject<AutocompleteHandle | null>;
  onAutocompleteOpen?: () => void;
  className?: string;
  /** Opens the location filter modal (remoteness + location search) from the button attached to
   * the search bar's right edge: this is the only place that modal opens from now that the
   * standalone category-button row is gone. */
  onLocationClick?: () => void;
  isLocationActive?: boolean;
  locationHasFilters?: boolean;
  locationHasExcluded?: boolean;
  /** Navbar live-index redesign (TEC navbar redesign Phase 1), flag-gated: shrinks the search
   * bar and location segment to 40px, and drops the location segment's background-fill "set"
   * state in favour of ink text + accent pin + a small accent dot. Omitted (default false) keeps
   * today's 52px bar and background-fill "set" state exactly as before this ticket. */
  liveIndex?: boolean;
  /** Phase 1 review fix F3: the fully-resolved label to show on the location segment when
   * liveIndex is true (the chosen place's name plus " +N", the remoteness label when only
   * remoteness is set, or the plain "Location" word when nothing is applied) computed by
   * DesktopFilterCategories via resolveLocationSegmentLabel. Ignored when liveIndex is false. */
  locationLabel?: string;
  /** Optional slot rendered at the end of this flex-1 cluster, after the fused location picker.
   * Putting a trailing control here (rather than as a sibling in the nav row) keeps it left-aligned
   * with the search bar: the search bar is capped (`max-w-[600px]`) in liveIndex mode, so the
   * flex-1 space that would otherwise push a sibling control right falls to the right of this slot
   * instead. The SRP header uses this for the Advanced Filters button. */
  children?: React.ReactNode;
}

/**
 * Search navbar component - contains logo and search bar
 * Used in DesktopFilterCategories for the left side of the search page header
 */
export const SearchNavbar: React.FC<SearchNavbarProps> = ({
  intl,
  formatMessage,
  clearSearch,
  autocompleteRef,
  onAutocompleteOpen,
  className = '',
  onLocationClick,
  isLocationActive = false,
  locationHasFilters = false,
  locationHasExcluded = false,
  liveIndex = false,
  locationLabel: locationLabelProp,
  children,
}) => {
  // Plain "Location" word: always the aria-label/fallback, and the flag-off display text
  // (unchanged since before the navbar live-index redesign). Flag on, DesktopFilterCategories
  // resolves the actual chosen place/remoteness name (F3) and passes it as locationLabel; this
  // falls back to the same word when nothing is applied.
  const locationWord = formatMessage({
    id: 'search.refinements.location',
    defaultMessage: 'Location',
    description: 'Filter label for location selection in search refinements',
  });
  const locationDisplayLabel = liveIndex && locationLabelProp ? locationLabelProp : locationWord;
  return (
    <div className={`flex items-center gap-3 min-w-0 flex-1 ${className}`}>
      {/* Logo */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <Logo clearSearch={clearSearch} variant="horizontal" locale={intl.locale} />
        <Logo clearSearch={clearSearch} variant="compact" locale={intl.locale} />
      </div>
      {/* Searchbar: no max-width, fills whatever space the row gives it (the old category-button
          row that used to eat this space is gone). The location picker is fused onto its right
          edge (Autocomplete's own right corners squared via `attached`, no gap between the two,
          the button's left border dropped) so the pair reads as one continuous search control
          rather than two separate nav items. */}
      <div
        className={`min-w-0 flex items-center ${liveIndex ? 'flex-1 max-w-[600px]' : 'flex-1 w-full'} ${
          liveIndex
            ? 'rounded-full transition-shadow duration-200 ease-out motion-reduce:transition-none focus-within:shadow-[0_0_0_4px_rgba(59,94,69,0.18),0_6px_28px_-6px_rgba(59,94,69,0.28)] dark:focus-within:shadow-[0_0_0_4px_rgba(152,207,168,0.16),0_6px_28px_-6px_rgba(152,207,168,0.30)]'
            : ''
        }`}
      >
        <div className="min-w-0 flex-1">
          <Autocomplete
            ref={autocompleteRef}
            onOpen={onAutocompleteOpen}
            attached={!!onLocationClick}
            sizeVariant={liveIndex ? 'compact40' : 'default'}
            placeholder={
              liveIndex
                ? formatMessage(navIndexMessages.searchPlaceholder)
                : formatMessage({
                    id: 'instantsearch.searchBoxLink.placeholder',
                    description: 'Placeholder for the search box',
                    defaultMessage: 'Search for jobs',
                  })
            }
          />
        </div>
        {onLocationClick && (
          <button
            type="button"
            onClick={onLocationClick}
            className={
              liveIndex
                ? `flex items-center justify-center gap-1.5 h-10 min-w-[40px] shrink-0 px-4 rounded-l-none rounded-r-full text-sm border-2 border-l-0 transition-all duration-200 cursor-pointer bg-jc-elevated border-jc-border ${
                    locationHasExcluded
                      ? 'text-red-700 dark:text-red-300'
                      : isLocationActive || locationHasFilters
                        ? 'text-jc-ink font-semibold'
                        : 'text-jc-ink-secondary font-medium'
                  }`
                : `flex items-center justify-center gap-1.5 h-[52px] min-w-[52px] shrink-0 px-4 rounded-l-none rounded-r-full text-sm font-medium border-2 border-l-0 transition-all duration-200 cursor-pointer ${
                    locationHasExcluded
                      ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-300 dark:border-red-700'
                      : isLocationActive || locationHasFilters
                        ? 'bg-[#C8DDCE] dark:bg-[#1A2E22] text-[#3B5E45] dark:text-[#98CFA8] border-[#3B5E45]/45 dark:border-[#98CFA8]/35'
                        : 'bg-white/90 dark:bg-[#1F1509]/90 text-[#1E1A14] dark:text-[#F2EADF] border-[#DDD5C8]/70 dark:border-[#4A3520]/80'
                  }`
            }
            aria-label={locationWord}
            aria-expanded={isLocationActive}
          >
            <MapPin
              weight="fill"
              className={`w-4 h-4 shrink-0 ${liveIndex && !locationHasExcluded && (isLocationActive || locationHasFilters) ? 'text-jc-accent' : ''}`}
            />
            <span className="hidden xl:inline whitespace-nowrap">
              {locationDisplayLabel}
              {liveIndex && !locationHasExcluded && (isLocationActive || locationHasFilters) ? (
                <span className="inline-block w-[5px] h-[5px] rounded-full bg-jc-accent ml-1 align-middle" aria-hidden />
              ) : null}
            </span>
          </button>
        )}
      </div>
      {children}
    </div>
  );
};
